import Table from '@tiptap/extension-table';

// Tiptap's default cellMinWidth. A column stored at exactly that width was never
// resized: documents saved while the min-width was misread as a width carry it.
export const TIPTAP_CELL_MIN_WIDTH = 25;

/**
 * Extended Table extension that reads column widths from <colgroup> elements
 * and applies them to cell colwidth attributes, allowing widths to persist
 * through the HTML → Markdown → HTML conversion cycle.
 */
export const TableWithColgroup = Table.extend({
	addAttributes() {
		return {
			...this.parent?.(),
		};
	},

	parseHTML() {
		return [
			{
				tag: 'table',
				getAttrs: (node) => {
					// Extract colgroup widths and store them temporarily
					// We'll apply them to cells in the parseHTML of TableCell/TableHeader
					if (typeof node === 'string') return null;

					const element = node as HTMLElement;
					const colgroup = element.querySelector('colgroup');

					if (colgroup) {
						const cols = colgroup.querySelectorAll('col');
						const widths: number[] = [];

						cols.forEach((col) => {
							const style = col.getAttribute('style');
							// `width:` only — Tiptap also writes `min-width: 25px` on every column.
							const widthMatch = style?.match(/(?:^|;)\s*width:\s*([^;]+)/);

							if (widthMatch) {
								const widthStr = widthMatch[1].trim();

								// Convert to pixels
								// For percentages, we can't know the exact pixel value without table width,
								// so we'll use a reasonable estimate or just store as-is
								if (widthStr.includes('%')) {
									// Assume a standard table width of 800px for percentage conversion
									const percent = Number.parseFloat(widthStr);
									widths.push(Math.round((percent / 100) * 800));
								} else if (widthStr.includes('px')) {
									widths.push(parseColwidth(widthStr));
								} else if (widthStr === 'auto') {
									widths.push(0); // 0 means auto-width in Tiptap
								} else {
									widths.push(0);
								}
							} else {
								widths.push(0);
							}
						});

						// Store widths on the element so TableCell can read them
						if (widths.length > 0 && widths.some((w) => w > 0)) {
							element.setAttribute('data-colwidths', JSON.stringify(widths));
						}
					}

					return null; // Return null to use default attributes
				},
			},
		];
	},
});

/** A pixel width to store as colwidth, or 0 (auto) for the never-resized minimum. */
export function parseColwidth(value: string): number {
	const px = Number.parseInt(value, 10);
	return Number.isNaN(px) || px === TIPTAP_CELL_MIN_WIDTH ? 0 : px;
}

export default TableWithColgroup;
